import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check, ShieldOff, Lock } from 'lucide-react';
import { useConsent } from '../context/ConsentContext';
import { getWithExpiry } from '../utils/storage';

/**
 * Bannière de consentement cookies (nLPD/RGPD).
 *  • Affichée uniquement si l'utilisateur n'a pas encore décidé.
 *  • 3 boutons d'importance visuelle identique : Accepter tout / Refuser tout / Personnaliser.
 *  • Aucun dark pattern : Refuser est aussi accessible visuellement qu'Accepter.
 *  • Bilingue FR/EN (langue détectée via localStorage `bodyup-language`).
 */
const T = (lang) => ({
  title: lang === 'en' ? 'Cookies & your privacy' : 'Cookies et votre vie privée',
  body: lang === 'en'
    ? 'We use only the cookies strictly necessary to operate the site. Analytics and marketing tools are disabled by default and will only run after your consent. You can change your mind at any time from the footer.'
    : "Nous utilisons uniquement les cookies strictement nécessaires au fonctionnement du site. Les outils d'analyse et de marketing sont désactivés par défaut et ne seront activés qu'après votre consentement. Vous pouvez modifier votre choix à tout moment depuis le pied de page.",
  accept: lang === 'en' ? 'Accept all' : 'Accepter tout',
  refuse: lang === 'en' ? 'Refuse all' : 'Refuser tout',
  customize: lang === 'en' ? 'Customize' : 'Personnaliser',
  necessary: lang === 'en' ? 'Necessary (always on)' : 'Nécessaire (toujours actif)',
  analytics: lang === 'en' ? 'Analytics' : 'Analytique',
  marketing: lang === 'en' ? 'Marketing' : 'Marketing',
  necessaryDesc: lang === 'en'
    ? 'Authentication, language, consent storage. Cannot be disabled.'
    : 'Authentification, langue, stockage du consentement. Ne peut pas être désactivé.',
  analyticsDesc: lang === 'en'
    ? 'Audience measurement to improve the site. No analytics tool is currently active — your consent here will only apply once such a tool is enabled by Body-Up.'
    : "Mesure d'audience pour améliorer le site. Aucun outil d'analyse n'est actif actuellement — votre consentement ici ne sera utilisé qu'à partir du moment où un tel outil sera mis en place par Body-Up.",
  marketingDesc: lang === 'en'
    ? 'Advertising audience and conversion. No marketing tool is currently active — your consent here will only apply once such a tool is enabled by Body-Up.'
    : "Audience publicitaire et conversion. Aucun outil marketing n'est actif actuellement — votre consentement ici ne sera utilisé qu'à partir du moment où un tel outil sera mis en place par Body-Up.",
  save: lang === 'en' ? 'Save preferences' : 'Enregistrer mes préférences',
  policyLink: lang === 'en' ? 'See cookie policy' : 'Voir la politique cookies',
  privacyLink: lang === 'en' ? 'Privacy policy' : 'Politique de confidentialité',
  close: lang === 'en' ? 'Close' : 'Fermer',
});

const Toggle = ({ checked, onChange, disabled, label }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    aria-pressed={checked}
    aria-label={label}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      disabled ? 'bg-zinc-700 cursor-not-allowed opacity-60' : checked ? 'bg-pink-500' : 'bg-zinc-600'
    }`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

export const ConsentBanner = () => {
  const { hasDecided, isOpen, acceptAll, refuseAll, setPreferences, closePreferences, consent } = useConsent();
  const location = useLocation();
  const lang = (typeof window !== 'undefined' && getWithExpiry('bodyup-language') === 'en') ? 'en' : 'fr';
  const t = T(lang);

  const [draftAnalytics, setDraftAnalytics] = useState(consent.analytics);
  const [draftMarketing, setDraftMarketing] = useState(consent.marketing);
  const [showCustom, setShowCustom] = useState(false);

  // Masquer la bannière sur les zones privées (admin / espace client)
  // L'utilisateur déjà authentifié a déjà fait son choix ; pas besoin de pollution visuelle.
  const isPrivateZone = location.pathname.startsWith('/admin')
    || location.pathname.startsWith('/espace-client')
    || location.pathname.startsWith('/mon-espace')
    || location.pathname.startsWith('/payment/success');

  if (isPrivateZone) return null;
  if (hasDecided && !isOpen) return null;

  const visible = !hasDecided || isOpen;

  const onCustomize = () => {
    setDraftAnalytics(consent.analytics);
    setDraftMarketing(consent.marketing);
    setShowCustom(true);
  };
  const onSave = () => {
    setPreferences({ analytics: draftAnalytics, marketing: draftMarketing });
    setShowCustom(false);
  };
  const onCloseBanner = () => {
    if (isOpen) closePreferences();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 24, stiffness: 220 }}
          className="fixed inset-x-2 bottom-2 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:left-auto z-[100]"
          role="dialog"
          aria-modal="false"
          aria-labelledby="consent-title"
          data-testid="consent-banner"
        >
          <div className="mx-auto sm:mx-0 max-w-3xl sm:max-w-sm rounded-2xl border border-pink-500/30 bg-zinc-950/95 backdrop-blur-xl shadow-2xl shadow-pink-500/10">
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-3 mb-3">
                <Cookie className="w-6 h-6 text-pink-400 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h2 id="consent-title" className="text-lg sm:text-xl font-bold text-white">{t.title}</h2>
                  {!showCustom && (
                    <p className="text-sm text-zinc-300 mt-1 leading-relaxed line-clamp-3 sm:line-clamp-none">{t.body}</p>
                  )}
                </div>
                {isOpen && (
                  <button onClick={onCloseBanner} className="text-zinc-500 hover:text-white transition-colors" aria-label={t.close} data-testid="consent-close-btn">
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              {!showCustom ? (
                <>
                  <div className="flex flex-col gap-2 mt-4">
                    <button
                      onClick={acceptAll}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white border border-white/10 bg-gradient-to-r from-pink-500 to-fuchsia-500 hover:opacity-90 transition-opacity"
                      data-testid="consent-accept-btn"
                    >
                      <Check className="w-4 h-4" /> {t.accept}
                    </button>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={refuseAll}
                        className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl text-xs sm:text-sm font-bold text-white border border-white/20 bg-zinc-800 hover:bg-zinc-700 transition-colors"
                        data-testid="consent-refuse-btn"
                      >
                        <ShieldOff className="w-3.5 h-3.5" /> {t.refuse}
                      </button>
                      <button
                        onClick={onCustomize}
                        className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl text-xs sm:text-sm font-bold text-white border border-white/20 bg-zinc-800 hover:bg-zinc-700 transition-colors"
                        data-testid="consent-customize-btn"
                      >
                        <Settings className="w-3.5 h-3.5" /> {t.customize}
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
                    <Link to={lang === 'en' ? '/cookie-policy' : '/politique-cookies'} className="hover:text-pink-400" data-testid="consent-cookie-policy-link">{t.policyLink}</Link>
                    <Link to={lang === 'en' ? '/privacy-policy' : '/politique-confidentialite'} className="hover:text-pink-400" data-testid="consent-privacy-policy-link">{t.privacyLink}</Link>
                  </div>
                </>
              ) : (
                <div className="space-y-3 mt-2" data-testid="consent-preferences">
                  <CategoryRow
                    title={t.necessary} desc={t.necessaryDesc}
                    icon={<Lock className="w-4 h-4 text-zinc-400" />}
                    checked disabled
                    testid="consent-toggle-necessary"
                  />
                  <CategoryRow
                    title={t.analytics} desc={t.analyticsDesc}
                    checked={draftAnalytics}
                    onChange={setDraftAnalytics}
                    testid="consent-toggle-analytics"
                  />
                  <CategoryRow
                    title={t.marketing} desc={t.marketingDesc}
                    checked={draftMarketing}
                    onChange={setDraftMarketing}
                    testid="consent-toggle-marketing"
                  />
                  <button
                    onClick={onSave}
                    className="w-full px-4 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-pink-500 to-fuchsia-500 hover:opacity-90 transition-opacity"
                    data-testid="consent-save-prefs-btn"
                  >
                    {t.save}
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const CategoryRow = ({ title, desc, checked, onChange, disabled, icon, testid }) => (
  <div className="flex items-start gap-3 p-3 rounded-xl border border-white/10 bg-zinc-900/60" data-testid={testid}>
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-sm font-bold text-white">{title}</span>
      </div>
      <p className="text-xs text-zinc-400 leading-relaxed">{desc}</p>
    </div>
    <Toggle checked={checked} onChange={onChange || (() => {})} disabled={disabled} label={title} />
  </div>
);

export default ConsentBanner;
